import { useEffect, useRef, useState } from "react";

import { Embed as EmbedBase, EMBED_DEFAULT_HEIGHT, EMBED_DEFAULT_WIDTH } from "@corca/tiptap";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { LineWrapper } from "@/components/tiptap/utils/line-wrapper/LineWrapper";
import { fetchEmbed } from "@/utils/embedService";
import { getIsMobile } from "@/utils/getIsMobile";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import { BlockResizerControls, BlockResizerRoot } from "./BlockResizer";
import { getUndoManager } from "./Collaboration";

import type { DocumentModel } from "@/models/DocumentModel";
import type { EmbedNodeAttrs } from "@corca/tiptap";
import type { NodeViewProps } from "@tiptap/core";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    embed: {
      appendEmbed: (position?: number, attrs?: { url: string }) => ReturnType;
      setEmbedWidth: (width: number) => ReturnType;
      setEmbedHeight: (height: number) => ReturnType;
      setEmbedUrl: (url: string) => ReturnType;
    };
  }
}

function isValidUrl(text: string): boolean {
  try {
    const url = new URL(text);
    return url.toString() === text && (url.protocol === "http:" || url.protocol === "https:");
  } catch {
    return false;
  }
}

type EmbedExtensionOptions = {
  doc: DocumentModel;
};

const EmbedWrapper = observer(function EmbedWrapper({
  extension,
  node,
  selected,
  updateAttributes,
  getPos,
}: NodeViewProps) {
  const { url, width, height, alignment } = node.attrs as EmbedNodeAttrs;
  const { doc } = extension.options as EmbedExtensionOptions;
  const containerRef = useRef<HTMLDivElement>(null);
  const [msg, setMsg] = useState<string | null>("Loading...");
  const [loaded, setLoaded] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const t = useTranslations("doc.embed");

  const isMobile = getIsMobile();
  const embedWidth = width ?? EMBED_DEFAULT_WIDTH;
  const embedHeight = height ?? EMBED_DEFAULT_HEIGHT;

  useEffect(() => {
    if (!url || !containerRef.current) return;

    fetchEmbed(url)
      .then((embedData) => {
        if (containerRef.current) {
          containerRef.current.innerHTML = embedData.html;
          setLoaded(true);
        }
      })
      .catch((e: Error) => {
        setMsg(e.message);
        setLoaded(false);
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
        }
      });
  }, [url]);

  return (
    <NodeViewWrapper>
      <LineWrapper className="group" doc={doc} getPos={getPos}>
        <BlockResizerRoot
          enabled={!isMobile && selected && doc.editable}
          className={twMerge(
            "sm:w-full [&>button]:sm:hidden",
            alignment === "left" && "mr-auto",
            alignment === "right" && "ml-auto",
            alignment === "center" && "mx-auto",
          )}
          onBlockResize={(size) => {
            updateAttributes({ width: size.width, height: size.height });
          }}
          onBlockResizeStart={() => setIsResizing(true)}
          onBlockResizeEnd={() => setIsResizing(false)}
        >
          <div className="w-0 h-0">&nbsp;</div>
          <BlockResizerControls
            getLabel={(index) => {
              return t(`resize-dot-label.${index}`, { index });
            }}
          />
          <div
            className={twMerge(
              "relative rounded-lg overflow-hidden",
              !loaded && "bg-gray-100 dark:bg-gray-800",
            )}
            style={{ width: embedWidth, height: embedHeight }}
          >
            <div
              ref={containerRef}
              className={twMerge(
                "w-full h-full",
                isResizing && "pointer-events-none",
                "[&_iframe]:!w-full [&_iframe]:!h-full",
              )}
            />
            {!loaded && (
              <div className="absolute inset-0 flex items-center justify-center text-center text-sm text-gray-500">
                {msg}
              </div>
            )}
          </div>
        </BlockResizerRoot>
      </LineWrapper>
    </NodeViewWrapper>
  );
});

export const Embed = EmbedBase.extend<EmbedExtensionOptions>({
  addNodeView() {
    return ReactNodeViewRenderer(EmbedWrapper);
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("embedPaste"),
        props: {
          handlePaste: (view, event) => {
            const text = event.clipboardData?.getData("text/plain")?.trim();

            if (!text || !isValidUrl(text)) {
              return false;
            }

            const embedDomain = new URL(text).hostname;
            const { selection } = view.state;
            const { $from, empty } = selection;
            const { parent } = $from;

            if (!empty || parent.type.name !== "paragraph" || parent.textContent.length > 0) {
              return false;
            }

            const paragraphPos = $from.before($from.depth);

            const insertTr = view.state.tr.insertText(text);
            view.dispatch(insertTr);

            fetchEmbed(text)
              .then((result) => {
                trackEvent("embed_insert", {
                  domain: embedDomain,
                  full_url: text,
                  is_rich_media: result.isRichMedia,
                });
                if (!result.isRichMedia) return;

                const paragraphNode = view.state.doc.nodeAt(paragraphPos);
                if (
                  !paragraphNode ||
                  paragraphNode.type.name !== "paragraph" ||
                  paragraphNode.textContent !== text
                ) {
                  return;
                }

                // start new undo stack right before turning link into embed
                getUndoManager(view.state).stopCapturing();

                const embed = view.state.schema.nodes.embed.create({
                  url: text,
                  width: result.width,
                  height: result.height,
                });
                const tr = view.state.tr.replaceWith(
                  paragraphPos,
                  paragraphPos + paragraphNode.nodeSize,
                  embed,
                );
                view.dispatch(tr);
              })
              .catch((e: Error) => {
                trackEvent("embed_insert", {
                  domain: embedDomain,
                  full_url: text,
                  error_message: e.message,
                });
              });

            return true;
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      appendEmbed:
        (position, attrs) =>
        ({ commands, state }) => {
          return commands.insertContentAt(
            position !== undefined ? position : state.selection.$to.end(1),
            {
              type: this.name,
              attrs,
            },
          );
        },
      setEmbedWidth:
        (width: number) =>
        ({ commands }) => {
          return commands.updateAttributes("embed", { width });
        },
      setEmbedHeight:
        (height: number) =>
        ({ commands }) => {
          return commands.updateAttributes("embed", { height });
        },
      setEmbedUrl:
        (url: string) =>
        ({ commands }) => {
          return commands.updateAttributes("embed", { url });
        },
    };
  },
});
